import React, { useState } from 'react';
import { Check, Star, Package, Truck, Phone, DollarSign } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import QuoteForm from './QuoteForm';

const PricingSection = () => {
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-gray-100 via-white to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Save UPTO 75% on international shipping to 185+ countries. No hidden fees, no surprises.
          </p>
        </div>

        {/* Monthly Subscription Section */}
        <div className="max-w-4xl mx-auto mb-12" data-aos="fade-up" data-aos-delay="200">
          <Card className="border-2 border-yellow-500 shadow-2xl overflow-hidden hover:shadow-3xl transition-all duration-300 hover:-translate-y-2">
            <div className="bg-gradient-to-r from-red-600 to-yellow-500 text-white py-4 px-6">
              <div className="flex items-center justify-center gap-2">
                <Star className="w-6 h-6 fill-current" />
                <h3 className="text-2xl font-bold">Monthly Shipping Plan for Regular Senders</h3>
                <Star className="w-6 h-6 fill-current" />
              </div>
            </div>
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <p className="text-xl text-gray-700 mb-4">
                  A simple subscription option for customers who ship regularly, locally or internationally.
                </p>
                <div className="inline-block bg-yellow-50 border-2 border-yellow-400 rounded-lg px-6 py-4 mb-4">
                  <p className="text-3xl font-bold text-gray-900 mb-1">Starting at CAD 19/month</p>
                  <p className="text-gray-600">For regular shippers</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Free Packaging Material</p>
                    <p className="text-sm text-gray-600">Boxes, tape, and packing supplies included</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Free Pickup & Drop-off Service</p>
                    <p className="text-sm text-gray-600">Convenient collection and delivery</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Discounted Shipping Rates</p>
                    <p className="text-sm text-gray-600">Up to 30% lower than usual rates</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Local & International</p>
                    <p className="text-sm text-gray-600">Suitable for all shipment types</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Minimum 2 Shipments per Week</p>
                    <p className="text-sm text-gray-600">Ideal for regular shippers only</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Priority Support</p>
                    <p className="text-sm text-gray-600">Dedicated assistance from Shippzy team</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>Important:</strong> This subscription covers packaging material, pickup, and drop-off services. Shipping charges apply separately at discounted rates (30% lower than usual). Minimum 2 shipments per week required. Terms and conditions may apply.
                </p>
              </div>

              <div className="text-center">
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button
                    onClick={() => window.location.href = '/contact'}
                    className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    Contact Us to Enroll
                  </Button>
                  <Button
                    onClick={() => window.location.href = 'tel:+14376160758'}
                    variant="outline"
                    className="border-2 border-red-600 text-red-600 hover:bg-red-50 px-10 py-6 text-lg"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Ask About Monthly Plan
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-4">Perfect for customers shipping regularly (minimum 2 times per week)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            For one-time or occasional shipments, get an instant quote based on your specific needs.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              onClick={() => setIsQuoteFormOpen(true)}
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3"
            >
              Get Instant Quote
            </Button>
            <a href="/shipping-cost-canada-to-india" className="inline-block">
              <Button
                variant="outline"
                className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-3"
              >
                View Pricing Table
              </Button>
            </a>
          </div>
        </div>
      </div>

      <QuoteForm isOpen={isQuoteFormOpen} onClose={() => setIsQuoteFormOpen(false)} />
    </section>
  );
};

export default PricingSection;